import { SetStateAction } from "react";
import { ChatMessage, ChatMessageType, Conversation, CrmIncidentType, ISupportFlow, SupportEntry, SupportUserInput } from "../../../../ApplicationCode/Common/Chatbot/api/models";
import { IAppStateContext } from "../../../../Core/State/ChatbotAppProvider";
import { t } from "i18next";
import uuid from "react-uuid";
import { SuccessForm, SupportRequestTextForm, SupportTypeForm } from "./EosAppSupport";
import { Answer } from "./Answer";
import { performApiRequest } from "../../../../ApplicationCode/Common/Chatbot/api";
import { Item } from "../../../../Model/PartnerModels";
import { HttpMethod } from "../../../../Core/Http/HttpHelper";
import { AxiosResponse } from "axios";
import { Guid } from "guid-typescript";
import { ISessionStateContext } from "../../../../Core/State/SessionStateContext";
import { ICustomerInformationContext } from "../../../../ApplicationCode/Customer/CustomerInformationContext";

enum SoSmartSupportStep {
    Email,
    SupportRequestTitle,
    SupportRequestDescription,
    End
}

export class SoSmartSupportFlow implements ISupportFlow {
    supportEntry: SupportEntry = new SupportEntry();
    context: ICustomerInformationContext | null = null; 

    constructor(customerInfoContext: ICustomerInformationContext)
    {
        this.context = customerInfoContext;
        this.supportEntry.tenantId = customerInfoContext.setupConfig.tenantId;
    }

    setApp(item: Item): void {
    }

    isErrorHandled(stepId: number): boolean {
        return false;
    }
    
    isFlowCompleted(stepId: number): boolean {
        let currentStep: SoSmartSupportStep = stepId;
        return (currentStep === SoSmartSupportStep.End);
    }

    handleFastActions(stepId: number, onClickHandler: (key: any, value: any) => void): JSX.Element | undefined {
        return undefined;
    }

    showUserMessage(stepId: number): boolean {
        return true;
    }

    handleElement(msg: ChatMessage, onUserInput: (key: any, value: any) => void): JSX.Element {
        let supportStep: SoSmartSupportStep;

        try {
            supportStep = this.convertMessageSubTypeToStep(msg);
        }
        catch {
            return <></>;
        }        
        
        switch (supportStep)
        {
            case SoSmartSupportStep.Email:
                return <Answer answer={{
                    answer: msg.content.toString(),
                    citations: [],
                }}
                onCitationClicked={() => {}}
                onOpenSupportRequestClick={() => {}}
                isLastIteraction={false}
            />
            case SoSmartSupportStep.SupportRequestTitle: 
                return <SupportRequestTextForm text={t("chatbot:supportRequestTextForm:title")!} />
            case SoSmartSupportStep.SupportRequestDescription: 
                return <SupportRequestTextForm text={t("chatbot:supportRequestTextForm:description")!} />
            case SoSmartSupportStep.End:
                return <SuccessForm />
        }
    }

    async loadRequiredData(supportId: string): Promise<void> {
        if(supportId)
            this.supportEntry = await this.getSupportEntry(supportId);
    }
    
    initializeStartStep(): number {
        return -1;
    }
    
    async handleStep(app: Item, stepId: number, userInput: SupportUserInput | undefined, appStateContext: IAppStateContext, conversation: Conversation, setMessages: (value: SetStateAction<ChatMessage[]>) => void): Promise<number> {
        let currentStep: SoSmartSupportStep = stepId;
        
        switch (currentStep)
        {
            case SoSmartSupportStep.Email:
                let result: boolean = this.validateEmail(userInput!.value);
                if (! result)
                {
                    let message = t("chatbot:chat:assistantEmailError",{email: userInput!.value});
                    throw new Error(message);
                }

                this.supportEntry.userEmail = userInput!.value;
                break;
            case SoSmartSupportStep.SupportRequestTitle:
                this.supportEntry.ticketTitle = userInput!.value;
                break;
            case SoSmartSupportStep.SupportRequestDescription:
                this.supportEntry.ticketDescription = userInput!.value;
                await this.createTicket(conversation.id);
                break;
            case SoSmartSupportStep.End:
                break;
        }

        conversation.messages.push(this.generateStepMessage(currentStep + 1));
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages);
        return 1;
    }

    private createBotRequestMessage(content: string, subType: string) : ChatMessage
    {
        return {
            id: uuid(),
            role: "assistant",
            content: content,
            date: new Date().toISOString(),
            type: ChatMessageType.BotRequest,
            subType: subType
        };
    }

    private generateStepMessage(step: SoSmartSupportStep) : ChatMessage {
        switch(step)
        {
            case SoSmartSupportStep.Email:
                return this.createBotRequestMessage(t("chatbot:chat:assistantEmailMessage"), "email");
            case SoSmartSupportStep.SupportRequestTitle: 
                return this.createBotRequestMessage("Phase 2 - Support request title", "supportRequestTitle");
                case SoSmartSupportStep.SupportRequestDescription: 
                    return this.createBotRequestMessage("Phase 2 - Support request description", "supportRequestDescription");
            case SoSmartSupportStep.End:
                return this.createBotRequestMessage("Phase 3 - Success", "success");
        }
    }

    private convertMessageSubTypeToStep(msg: ChatMessage) : SoSmartSupportStep {
        switch (msg.subType)
        {
            case "email":
                return SoSmartSupportStep.Email;
            case "supportRequestTitle":
                return SoSmartSupportStep.SupportRequestTitle;
                case "supportRequestDescription":
                    return SoSmartSupportStep.SupportRequestDescription;
            case "success":
                return SoSmartSupportStep.End;
        }

        throw new Error("Invalid message subtype");
    }
    
    private async createTicket(conversationId: string): Promise<boolean>  {
        if(this.supportEntry.id === Guid.EMPTY)
            this.supportEntry = await this.insertSupportEntry();
        else
            this.supportEntry = await this.updateSupportEntry();

        try {
            let url = "/api/CRM/incident/user/support/create/" + this.supportEntry.id + "/" + conversationId;
            const response: AxiosResponse<any, any> = await performApiRequest(url, HttpMethod.POST);

            if (! (response.status === 200))
                throw new Error("");

            return true;
        }   
        catch
        {
            throw new Error(t("chatbot:openSupportRequest:ticketCreationError")!);
        }
        
    }
    
    private async getSupportEntry(supportId: string): Promise<SupportEntry> {
        const response: AxiosResponse<any, any> = await performApiRequest("/api/EosChat/support/entry/" + supportId, HttpMethod.GET);
        if(response.status !== 200)
            throw new Error("Error fetching support entry");

        return response.data;
    }
    
    private async insertSupportEntry(): Promise<SupportEntry> {
        const response: AxiosResponse<any, any> = await performApiRequest("/api/EosChat/support/entry", HttpMethod.POST, this.supportEntry);
        if(response.status !== 200)
            throw new Error("Error creating support entry");

        return response.data;
    }

    private async updateSupportEntry(): Promise<SupportEntry> {
        try {
            const response: AxiosResponse<any, any> = await performApiRequest("/api/EosChat/support/entry/" + this.supportEntry.systemId, HttpMethod.PATCH, this.supportEntry);

            if (! (response.status == 200))
                throw new Error("");
            
            return response.data;
        }   
        catch
        {
            throw new Error(t("chatbot:openSupportRequest:updateSupportEntryError")!);
        }
    }

    private validateEmail(value: string) : boolean {
        let isValid = value.toLocaleLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

        if (! isValid)
            return false;
        return true;
    }    
}